import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { FaNotesMedical } from "react-icons/fa6";
import { FaColumns, FaExpand, FaCompress } from "react-icons/fa";
import { GoPencil } from "react-icons/go";
import ModifyColumns from "../Tools/ModifyColumns";
import {
  AddTableFilterComponent,
  apiRequestBodyBuilder,
} from "./../config/tableHelper";
import { BiHide, BiSolidBulb } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { RenderTable } from "../config/tableWrapper";
import { GROUP_ADDTABLE_COLUMNS } from "../Components/AddTableCols";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Insights Skeleton consistent with PaidAdsPage aesthetic
function InsightsSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full p-4 sm:p-5 space-y-4 animate-pulse">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="flex flex-col sm:flex-row items-start gap-4 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0"
        >
          <div className="w-12 h-12 rounded-xl bg-gray-200 shrink-0" />
          <div className="w-36 shrink-0 pt-2">
            <div className="h-4 bg-gray-200 rounded w-24" />
          </div>
          <div className="flex-grow pt-2 w-full space-y-2">
            <div className="h-3.5 bg-gray-200 rounded w-full" />
            <div
              className="h-3 bg-gray-100 rounded"
              style={{ width: `${65 + (item % 3) * 15}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Array of unique [Icon, ColorStyle] Tuples for each group
const GROUP_STYLE_TUPLES = [
  [
    <svg
      key="stack"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>,
    {
      bgIcon: "bg-blue-50 text-blue-600 border border-blue-100",
      titleColor: "text-blue-600",
    },
  ],
  [
    <svg
      key="folder"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    </svg>,
    {
      bgIcon: "bg-emerald-50 text-emerald-600 border border-emerald-100",
      titleColor: "text-emerald-600",
    },
  ],
  [
    <svg
      key="cube"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>,
    {
      bgIcon: "bg-purple-50 text-purple-600 border border-purple-100",
      titleColor: "text-purple-600",
    },
  ],
  [
    <svg
      key="target"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" />
    </svg>,
    {
      bgIcon: "bg-amber-50 text-amber-600 border border-amber-100",
      titleColor: "text-amber-600",
    },
  ],
  [
    <svg
      key="tag"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      />
    </svg>,
    {
      bgIcon: "bg-rose-50 text-rose-600 border border-rose-100",
      titleColor: "text-rose-600",
    },
  ],
  [
    <svg
      key="chart"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>,
    {
      bgIcon: "bg-cyan-50 text-cyan-600 border border-cyan-100",
      titleColor: "text-cyan-600",
    },
  ],
  [
    <svg
      key="users"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>,
    {
      bgIcon: "bg-indigo-50 text-indigo-600 border border-indigo-100",
      titleColor: "text-indigo-600",
    },
  ],
  [
    <svg
      key="sparkle"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>,
    {
      bgIcon: "bg-teal-50 text-teal-600 border border-teal-100",
      titleColor: "text-teal-600",
    },
  ],
  [
    <svg
      key="lightning"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>,
    {
      bgIcon: "bg-yellow-50 text-yellow-700 border border-yellow-200",
      titleColor: "text-yellow-700",
    },
  ],
  [
    <svg
      key="shield"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>,
    {
      bgIcon: "bg-violet-50 text-violet-600 border border-violet-100",
      titleColor: "text-violet-600",
    },
  ],
  [
    <svg
      key="compass"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="9" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>,
    {
      bgIcon: "bg-lime-50 text-lime-700 border border-lime-200",
      titleColor: "text-lime-700",
    },
  ],
  [
    <svg
      key="pie"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
      />
    </svg>,
    {
      bgIcon: "bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-100",
      titleColor: "text-fuchsia-600",
    },
  ],
  [
    <svg
      key="globe"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3a15.3 15.3 0 014 9 15.3 15.3 0 01-4 9 15.3 15.3 0 01-4-9 15.3 15.3 0 014-9z"
      />
    </svg>,
    {
      bgIcon: "bg-sky-50 text-sky-600 border border-sky-100",
      titleColor: "text-sky-600",
    },
  ],
  [
    <svg
      key="bookmark"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
      />
    </svg>,
    {
      bgIcon: "bg-orange-50 text-orange-600 border border-orange-100",
      titleColor: "text-orange-600",
    },
  ],
  [
    <svg
      key="key"
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>,
    {
      bgIcon: "bg-stone-50 text-stone-700 border border-stone-200",
      titleColor: "text-stone-700",
    },
  ],
];

const getGroupTuple = (index = 0) => {
  const tupleIndex = index % GROUP_STYLE_TUPLES.length;
  const [icon, style] = GROUP_STYLE_TUPLES[tupleIndex];
  return {
    icon,
    bgIcon: style.bgIcon,
    titleColor: style.titleColor,
  };
};

const CustomTable = ({
  initialDateRange,
  insightAvailable = false,
  PageType = null,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupNames, setGroupNames] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [columns, setColumns] = useState(GROUP_ADDTABLE_COLUMNS);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [extraColumns, setExtraColumns] = useState([]);
  const [extraData, setExtraData] = useState([]);
  const [insightsArray, setInsightsArray] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [isEditingInsight, setIsEditingInsight] = useState(false);
  const [editedInsights, setEditedInsights] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const navigate = useNavigate();
  const columnRef = useRef(null);
  const filterRef = useRef(null);

  const toggleFullScreen = () => setIsFullScreen((prev) => !prev);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  const fetchExtraColumns = async () => {
    try {
      const res = await axios.post(
        "https://api.confidanto.com/column-information/get",
        apiRequestBodyBuilder(initialDateRange),
      );
      const newCols = res.data.map((col) => ({
        id: `extra-${col.id}`,
        title: col.column_name,
        key: col.column_name,
        visible: true,
        category: "Notes",
        isLocked: false,
        type: "string",
      }));
      setExtraColumns(newCols);
      setExtraData(res.data);
      setColumns([...GROUP_ADDTABLE_COLUMNS, ...newCols]);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchGroupNames = async () => {
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange);
      const resInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!resInfo.ok) return [];
      const groupType = await resInfo.json();
      requestBody.type =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      const res = await axios.post(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        requestBody,
      );

      const fetchedGroups = res.data.groups.map((g) => ({
        id: g.group_id,
        name: g.group_name,
      }));

      setGroupNames(fetchedGroups);
      return fetchedGroups;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const fetchData = async (fetchedGroups = []) => {
    const cid = localStorage.getItem("customer_id") ?? "";
    try {
      let responseData;
      if (cid === "7948821642") {
        const res = await axios.post(
          "https://api.confidanto.com/campaign-group2/dummy-campaign-grouped-metric",
          {
            customer_id: cid,
            project_id: localStorage.getItem("project_id"),
            email: localStorage.getItem("email"),
            group_ids: ["KZ5PZPG3MS"],
            start_date: "2020-10-10",
            end_date: "2034-10-12",
            limit: 100,
            offset: 1000,
          },
        );
        responseData = res.data.map((i) => ({
          group_name: i.group_name,
          ...i.group_totals,
        }));
      } else {
        const res = await axios.post(
          "https://api.confidanto.com/campaign-group/datewise-data",
          apiRequestBodyBuilder(initialDateRange, {
            group_names: fetchedGroups.map((i) => i.name),
          }),
        );
        responseData = res.data;
      }
      setData(responseData);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      if (!initialDateRange[0]?.startDate || !initialDateRange[0]?.endDate)
        return;

      setLoading(true);
      try {
        const groups = await fetchGroupNames();
        await fetchData(groups);
        await fetchExtraColumns();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    setAppliedFilters([]);
    setInsightsArray([]);
    setShowInsights(false);
    setIsEditingInsight(false);

    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["group"] = false;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error(e);
    }
  }, [initialDateRange]);

  const getFilteredData = () => {
    if (appliedFilters.length === 0) return data;
    return data.filter((row) =>
      appliedFilters.every((filter) => {
        const rowValue =
          typeof row[filter.key] === "object"
            ? row[filter.key]?.value
            : row[filter.key];
        if (rowValue === null || rowValue === undefined) return false;
        if (filter.keyType === "number") {
          const rowNum = parseFloat(rowValue),
            filterNum = parseFloat(filter.value);
          if (isNaN(rowNum) || isNaN(filterNum)) return false;
          if (filter.comparison === ">") return rowNum > filterNum;
          if (filter.comparison === "<") return rowNum < filterNum;
          if (filter.comparison === "=") return rowNum === filterNum;
          return true;
        } else if (filter.keyType === "boolean") {
          return filter.value.includes(String(rowValue));
        } else {
          return rowValue
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        }
      }),
    );
  };

  const fetchInsights = async () => {
    setInsightsLoading(true);
    setInsightsArray([]);

    let requestBody = apiRequestBodyBuilder(initialDateRange, {
      insight_type: "group",
    });

    axios
      .post(
        "https://api.confidanto.com/get-insights/custom-reporting-insights",
        requestBody,
      )
      .then((res) => {
        setInsightsArray(res.data || []);
      })
      .catch((err) => console.error("Error Insights Group: ", err))
      .finally(() => {
        setInsightsLoading(false);
      });
  };

  const updateInsight = async (newInsights) => {
    try {
      const baseRequestInfo = apiRequestBodyBuilder(initialDateRange, {});

      const updatePromises = newInsights.map((insight) => {
        const requestBody = {
          id: insight.id,
          ad_account_id: insight.ad_account_id || baseRequestInfo.ad_account_id,
          insight_type: insight.insight_type || "group",
          entity_name: insight.entity_name,
          insight_text: insight.insight_text,
        };

        return axios.post(
          "https://api.confidanto.com/get-insights/update-custom-reporting-insights",
          requestBody,
        );
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating insights: ", error);
      alert("Failed to save some insights. Please try again.");
    }
  };

  const handleToggleInsights = () => {
    if (!showInsights && !insightsArray.length) {
      fetchInsights();
    }
    const newShowState = !showInsights;
    setShowInsights(newShowState);

    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["group"] = newShowState;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error("Failed to save insight state", e);
    }
  };

  const handleEditClick = () => {
    setEditedInsights(insightsArray.map((insight) => ({ ...insight })));
    setIsEditingInsight(true);
  };

  const handleInsightChange = (index, newValue) => {
    const newArr = [...editedInsights];
    newArr[index] = { ...newArr[index], insight_text: newValue };
    setEditedInsights(newArr);
  };

  const handleSaveInsight = async () => {
    setInsightsLoading(true);
    await updateInsight(editedInsights);
    setInsightsArray(editedInsights);
    setIsEditingInsight(false);
    setInsightsLoading(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnRef.current && !columnRef.current.contains(e.target))
        setShowColumnsMenu(false);
      if (
        filterRef.current &&
        !filterRef.current.contains(e.target) &&
        !e.target.closest(".groupwise-select-menu")
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const routeMap = {
    daily: "/google-ads/reporting/daily-reporting",
    weekly: "/google-ads/reporting/weekly-reporting",
    monthly: "/google-ads/reporting/monthly-reporting",
    null: `/project-details/${localStorage.getItem("project_id")}`,
  };

  const filteredData = getFilteredData();
  const activeInsightsData = isEditingInsight ? editedInsights : insightsArray;

  const content = (
    <div
      className={`font-roboto ${
        isFullScreen
          ? "fixed inset-0 w-screen h-screen z-[999999] overflow-y-auto bg-gray-100 p-6 shadow-2xl"
          : "p-4 bg-gray-100"
      }`}
    >
      <div className="flex justify-between my-1.5 w-full">
        <div
          className={`transition-opacity duration-300 ${
            loading ? "pointer-events-none opacity-50" : ""
          }`}
        >
          <AddTableFilterComponent
            data={data}
            filterRef={filterRef}
            showFilterDropdown={showFilterDropdown}
            setShowFilterDropdown={setShowFilterDropdown}
            appliedFilters={appliedFilters}
            handleRemoveFilter={(f) =>
              setAppliedFilters(appliedFilters.filter((i) => i.id !== f.id))
            }
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            columns={columns}
            onApplyStaticFilter={(f) => {
              setAppliedFilters((p) => [...p, f]);
              setShowFilterDropdown(false);
            }}
          />
        </div>
        <div className="flex space-x-2 relative" ref={columnRef}>
          <button
            disabled={loading}
            className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center hover:bg-slate-100 ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() =>
              navigate("/google-ads/AddNotes", {
                state: { from: routeMap[PageType] },
              })
            }
          >
            <FaNotesMedical className="text-sm" />
            <span className="text-sm">notes</span>
          </button>
          <button
            disabled={loading}
            className={`px-3 py-1.5 rounded flex flex-col items-center hover:bg-slate-100 transition-all ${
              isFullScreen
                ? "bg-blue-100 text-blue-600"
                : "bg-transparent text-gray-600"
            } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={toggleFullScreen}
            title={isFullScreen ? "Exit Fullscreen (Esc)" : "Fullscreen"}
          >
            {isFullScreen ? (
              <FaCompress className="text-sm" />
            ) : (
              <FaExpand className="text-sm" />
            )}
            <span className="text-sm">
              {isFullScreen ? "Collapse" : "Expand"}
            </span>
          </button>
          <button
            disabled={loading}
            className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center hover:bg-slate-100 ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() => setShowColumnsMenu(!showColumnsMenu)}
          >
            <FaColumns className="text-sm" />
            <span className="text-sm">Columns</span>
          </button>
          {showColumnsMenu && (
            <ModifyColumns
              columns={columns}
              setColumns={setColumns}
              setShowColumnsMenu={setShowColumnsMenu}
              colsLimit={13}
            />
          )}
        </div>
      </div>

      {/* Table Section with Skeleton Loader */}
      {loading ? (
        <div className="mt-3">
          <TableSkeleton />
        </div>
      ) : filteredData.length > 0 ? (
        <div className="overflow-x-auto bg-white rounded shadow mt-3">
          <RenderTable
            columns={columns}
            data={filteredData}
            extraColumns={extraColumns}
            extraData={extraData}
            enableExpandCollapse={initialDateRange.length > 1}
          />
        </div>
      ) : (
        <div className="flex justify-center items-center h-60 mt-6">
          <h2>No Data Found</h2>
        </div>
      )}

      {insightAvailable && (
        <div className="w-full my-3">
          <button
            disabled={loading}
            onClick={handleToggleInsights}
            className={`flex items-center justify-center gap-1.5 w-auto bg-[#283593] text-white font-semibold py-1.5 px-3 rounded-lg shadow-md transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75 ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1a237e]"
            }`}
          >
            {showInsights ? (
              <>
                <BiHide size="1.2em" />
                <span className="text-sm">Hide Insights</span>
              </>
            ) : (
              <>
                <BiSolidBulb size="1.2em" />
                <span className="text-sm">Show Insights</span>
              </>
            )}
          </button>

          <div
            className={`w-full transition-all duration-500 ease-in-out overflow-hidden ${
              showInsights ? "opacity-100 mt-2" : "max-h-0 opacity-0"
            }`}
          >
            {insightsLoading ? (
              <InsightsSkeleton />
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full relative overflow-hidden">
                {!isEditingInsight && insightsArray.length > 0 && (
                  <button
                    onClick={handleEditClick}
                    className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow hover:bg-blue-700 transition-transform hover:scale-105 z-10"
                    title="Edit Insights"
                  >
                    <GoPencil size={16} />
                  </button>
                )}

                {insightsArray.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {activeInsightsData.map((item, index) => {
                      const { icon, bgIcon, titleColor } = getGroupTuple(index);
                      return (
                        <div
                          key={index}
                          className="p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4 hover:bg-slate-50/50 transition-colors"
                        >
                          <div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${bgIcon}`}
                          >
                            {icon}
                          </div>

                          <div
                            className={`w-36 shrink-0 font-bold text-base pt-2.5 sm:pt-2 ${titleColor}`}
                          >
                            {item.entity_name}
                          </div>

                          <div className="flex-grow text-xs sm:text-sm text-slate-600 leading-relaxed pt-2.5 sm:pt-2 w-full">
                            {isEditingInsight ? (
                              <div className="grid w-full">
                                <textarea
                                  className="col-start-1 col-end-2 row-start-1 row-end-2 w-full resize-none overflow-hidden bg-white border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-md p-2 outline-none transition-all duration-200 text-slate-700 leading-relaxed"
                                  value={item.insight_text}
                                  onChange={(e) =>
                                    handleInsightChange(index, e.target.value)
                                  }
                                  rows={3}
                                />
                              </div>
                            ) : (
                              <span>{item.insight_text}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-6 text-center text-sm text-red-500">
                    No Insights found for above data
                  </div>
                )}

                {isEditingInsight && (
                  <div className="flex justify-end gap-3 p-4 bg-slate-50 border-t border-gray-100">
                    <button
                      onClick={() => setIsEditingInsight(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveInsight}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#283593] border border-transparent rounded-lg shadow-sm hover:bg-[#1a237e] focus:outline-none transition-all"
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return isFullScreen ? createPortal(content, document.body) : content;
};

export default CustomTable;
